import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import SplashScreen from './components/SplashScreen'
import ProfileFavoritesPage from './pages/ProfileFavoritesPage'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import AdminProtectedRoute from './components/AdminProtectedRoute'
import LoadingSpinner from './components/LoadingSpinner'

// Critical pages (loaded immediately)
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import VerifyEmailRequiredPage from './pages/VerifyEmailRequiredPage'
import ListingsPage from './pages/ListingsPage'
import HomePageWrapper from './pages/HomePageWrapper'

// Lazy load other pages
const TeamPage = lazy(() => import('./pages/TeamPage'))
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'))
const CreateListingPage = lazy(() => import('./pages/CreateListingPage'))
const GroupsPage = lazy(() => import('./pages/GroupsPage'))
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
const BannerDetailPage = lazy(() => import('./pages/BannerDetailPage'))
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'))
const UserListingsPage = lazy(() => import('./pages/UserListingsPage'))
const MessagesPage = lazy(() => import('./pages/MessagesPage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const EmailThreadsPage = lazy(() => import('./pages/EmailThreadsPage'))
const NewsPage = lazy(() => import('./pages/NewsPage'))
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'))
const AISearchPage = lazy(() => import('./pages/AISearchPage'))
const AISettingsPage = lazy(() => import('./pages/AISettingsPage'))
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'))
// Reels page (mobile-first route)
const ReelsPage = lazy(() => import('./pages/ReelsPage'))

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/" element={<Layout />}>
               <Route index element={<HomePageWrapper />} />
              <Route path="listings" element={<ListingsPage />} />
              <Route path="listings/:id" element={<Suspense fallback={<LoadingSpinner />}><ListingDetailPage /></Suspense>} />
              <Route path="listings/new" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><CreateListingPage /></Suspense></ProtectedRoute>} />
              <Route path="listings/:id/edit" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><CreateListingPage /></Suspense></ProtectedRoute>} />
              <Route path="groups" element={<Suspense fallback={<LoadingSpinner />}><GroupsPage /></Suspense>} />
              <Route path="groups/:id" element={<Suspense fallback={<LoadingSpinner />}><GroupDetailPage /></Suspense>} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="verify-email-required" element={<ProtectedRoute requireEmailVerification={false}><VerifyEmailRequiredPage /></ProtectedRoute>} />
              <Route path="news" element={<Suspense fallback={<LoadingSpinner />}><NewsPage /></Suspense>} />
              <Route path="news/:id" element={<Suspense fallback={<LoadingSpinner />}><NewsDetailPage /></Suspense>} />
              <Route path="search" element={<Suspense fallback={<LoadingSpinner />}><SearchResultsPage /></Suspense>} />
              <Route path="ai-search" element={<Suspense fallback={<LoadingSpinner />}><AISearchPage /></Suspense>} />
              <Route path="team" element={<AdminProtectedRoute><Suspense fallback={<LoadingSpinner />}><TeamPage /></Suspense></AdminProtectedRoute>} />
              <Route path="admin" element={<AdminProtectedRoute><Suspense fallback={<LoadingSpinner />}><AdminDashboardPage /></Suspense></AdminProtectedRoute>} />
              <Route path="reels" element={<Suspense fallback={<LoadingSpinner />}><ReelsPage /></Suspense>} />
              <Route path="admin/ai-settings" element={<AdminProtectedRoute><Suspense fallback={<LoadingSpinner />}><AISettingsPage /></Suspense></AdminProtectedRoute>} />
              <Route path="admin/banners/:id" element={<Suspense fallback={<LoadingSpinner />}><BannerDetailPage /></Suspense>} />
              <Route path="profile/:userId" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><UserProfilePage /></Suspense></ProtectedRoute>} />
              <Route path="profile" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><UserProfilePage /></Suspense></ProtectedRoute>} />
              <Route path="my-listings" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><UserListingsPage /></Suspense></ProtectedRoute>} />
              <Route path="messages" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><MessagesPage /></Suspense></ProtectedRoute>} />
              <Route path="messages/:id" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><ChatPage /></Suspense></ProtectedRoute>} />
              <Route path="messages/threads" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><EmailThreadsPage /></Suspense></ProtectedRoute>} />
              <Route path="profile/favorites" element={<ProtectedRoute><Suspense fallback={<LoadingSpinner />}><ProfileFavoritesPage /></Suspense></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <SplashScreen />
        </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
